﻿import { AuthContext } from '@core/shared';
import { IsAllowedResponse, OptionsApi, WebinarSecuritySettings } from '@core/swagger-client';
import {
    Button,
    Close,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContentHeader,
    ErrorMessagePage,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    RightCheck
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ShareEventWidget } from '../../molecules';

interface WebinarSecuritySettingsEx extends WebinarSecuritySettings {
    version: number;
}

export const WebinarSecurity = () => {
    const { t } = useTranslation();
    const { webinarId } = useParams({ strict: false });

    const [securitySettings, setSecuritySettings] = useState<WebinarSecuritySettingsEx>();
    const [savedSecuritySettings, setSavedSecuritySettings] = useState<WebinarSecuritySettingsEx>();

    const [disallowFilter, setDisallowFilter] = useState<string>('');
    const [allowFilter, setAllowFilter] = useState<string>('');

    const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');

    const [checkResults, setCheckResults] = useState<IsAllowedResponse[]>([]);
    const [checkIsAllowedVersion, setCheckIsAllowedVersion] = useState<number>(0);
    const [checkedIsAllowedVersion, setCheckedIsAllowedVersion] = useState<number>(0);
    const [customEmailAddress, setCustomEmailAddress] = useState<string>('');
    const [customEmailAddressChecked, setCustomEmailAddressChecked] = useState<string>('');
    const [isCheckingIsAllowed, setIsCheckingIsAllowed] = useState<boolean>(false);

    const { data: webinar, error: webinarError, isLoading: isWebinarLoading } = useQuery(OptionsApi.webinar.getEdit(webinarId!));
    const {
        data: securitySettingsData,
        error: securitySettingsError,
        isLoading: isSecuritySettingsLoading
    } = useQuery(OptionsApi.webinarSecurity.getSettings(webinarId!));

    useEffect(() => {
        if (securitySettingsData) {
            const settings = { ...securitySettingsData, version: Date.now() };
            setSecuritySettings(settings);
            setSavedSecuritySettings(settings);

            setDisallowFilter(settings.disallowEmailAddresses.join('\n'));
            setAllowFilter(settings.allowEmailAddresses.join('\n'));

            setCheckIsAllowedVersion(settings.version);
        }
    }, [securitySettingsData]);

    const updateSettingsMutation = useMutation(OptionsApi.webinarSecurity.updateSettings());
    useEffect(() => {
        if (!securitySettings || !savedSecuritySettings) {
            return;
        }

        if (securitySettings.version === savedSecuritySettings.version) {
            return;
        }

        setSaveState('saving');
        const handle = setTimeout(() => {
            updateSettingsMutation.mutate(
                {
                    webinarId: webinarId!,
                    settings: securitySettings
                },
                {
                    onSuccess: () => {
                        setCheckIsAllowedVersion(securitySettings.version);
                        setSavedSecuritySettings(securitySettings);
                        setSaveState('saved');
                    },
                    onError: () => {
                        setSaveState('error');
                    }
                }
            );
        }, 500);

        return () => clearTimeout(handle);
    }, [securitySettings, savedSecuritySettings]);

    const account = useContext(AuthContext).mainAccount;

    const updateAllow = (allow: string) => {
        setAllowFilter(allow);
        setSecuritySettings(
            (prev) =>
                prev && {
                    ...prev,
                    allowEmailAddresses: allow
                        .replace('\r', '')
                        .split('\n')
                        .map((x) => x.trim())
                        .map((x) => x.toLowerCase())
                        .filter((x) => x),
                    version: prev.version + 1
                }
        );
    };

    const updateDisallow = (disallow: string) => {
        setDisallowFilter(disallow);
        setSecuritySettings(
            (prev) =>
                prev && {
                    ...prev,
                    disallowEmailAddresses: disallow
                        .replace('\r', '')
                        .split('\n')
                        .map((x) => x.trim())
                        .map((x) => x.toLowerCase())
                        .filter((x) => x),
                    version: prev.version + 1
                }
        );
    };

    const toggleVerificationRequired = () => {
        setSecuritySettings((prev) => prev && { ...prev, requireVerifiedAccounts: !prev.requireVerifiedAccounts, version: prev.version + 1 });
    };

    const toggleSingleViewerSession = () => {
        setSecuritySettings((prev) => prev && { ...prev, singleViewerSession: !prev.singleViewerSession, version: prev.version + 1 });
    };

    const toggleAllowAnonymousViewers = () => {
        setSecuritySettings((prev) => prev && { ...prev, allowAnonymousViewers: !prev.allowAnonymousViewers, version: prev.version + 1 });
    };

    const generateAuthorizationCode = () => {
        const newCode = Math.random().toString(36).substring(2, 10).toUpperCase();
        setSecuritySettings((prev) => prev && { ...prev, anonymousViewerAuthorizationCode: newCode, version: prev.version + 1 });
    };

    const checkIsAllowedMutation = useMutation(
        OptionsApi.webinarSecurity.checkIsAllowed({
            onSuccess: (data) => {
                setCheckResults(data ?? []);
                setIsCheckingIsAllowed(false);
            }
        })
    );
    useEffect(() => {
        if (!securitySettings) {
            return;
        }

        if (checkIsAllowedVersion === checkedIsAllowedVersion && customEmailAddress === customEmailAddressChecked) {
            return;
        }

        setIsCheckingIsAllowed(true);

        const emailAddresses = ['test@gmail.com', 'test@outlook.com', 'noreply@onlinewebinar.com'];
        if (account?.profile?.emailAddress) {
            emailAddresses.push(account.profile.emailAddress);
        }
        if (customEmailAddress) {
            emailAddresses.push(customEmailAddress);
        }

        const handle = setTimeout(() => {
            setCheckedIsAllowedVersion(checkIsAllowedVersion);
            setCustomEmailAddressChecked(customEmailAddress);
            checkIsAllowedMutation.mutate({ webinarId: webinarId!, emailAddresses });
        }, 500);

        return () => clearTimeout(handle);
    }, [account, checkIsAllowedVersion, checkedIsAllowedVersion, customEmailAddress, customEmailAddressChecked, securitySettings]);

    const error = webinarError || securitySettingsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || isSecuritySettingsLoading || !securitySettings) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <div className="px:4 md:px-6 pt-4 md:pt-10">
                <div className="max-w-4xl">
                    <BackToOriginLink fallback="overview" />
                    <div>
                        {saveState === 'saved' && <p className="text-success mb-4">{t('EditSurvey.Save.Saved')}</p>}
                        {saveState === 'saving' && <p className="text-orange-500 mb-4">{t('EditSurvey.Save.Saving')}</p>}
                        {saveState === 'error' && <p className="text-warning mb-4">{t('EditSurvey.Save.Error')}</p>}
                    </div>
                    <ContentHeader contentHeader={t('Security.PageHeading')} testId="webinar-security" />
                    <p className="mb-6">{t('Security.Introtext')}</p>
                    <div className="pl-10 mb-4">
                        <input
                            type="checkbox"
                            role="switch"
                            id="singleViewerSession"
                            checked={securitySettings.singleViewerSession}
                            onChange={() => toggleSingleViewerSession()}
                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                        />
                        <label htmlFor="singleViewerSession" className="inline-block">
                            {t('Security.Switch.OneViewerSession')}
                        </label>
                    </div>
                    <div className="pl-10 mb-4">
                        <input
                            type="checkbox"
                            role="switch"
                            id="requireVerifiedAccounts"
                            checked={securitySettings.requireVerifiedAccounts}
                            onChange={() => toggleVerificationRequired()}
                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                        />
                        <label htmlFor="requireVerifiedAccounts" className="inline-block">
                            {t('Security.Switch.RequireVerified')}
                        </label>
                    </div>
                    <div className="pl-10 mb-4">
                        <input
                            type="checkbox"
                            role="switch"
                            id="allowAnonymousViewers"
                            checked={securitySettings.allowAnonymousViewers}
                            onChange={() => toggleAllowAnonymousViewers()}
                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                        />
                        <label htmlFor="allowAnonymousViewers" className="inline-block">
                            {t('Security.Switch.AllowAnonymous')}
                        </label>
                    </div>
                    {securitySettings.allowAnonymousViewers && (
                        <>
                            <div className="pl-10 mb-6">
                                <Input
                                    label={t('Security.AnonymousViewerAuthorizationCodeLabel')}
                                    value={securitySettings.anonymousViewerAuthorizationCode}
                                    onChange={(e) =>
                                        setSecuritySettings(
                                            (prev) => prev && { ...prev, anonymousViewerAuthorizationCode: e.target.value, version: prev.version + 1 }
                                        )
                                    }
                                />
                                <Button variant="primary" onClick={generateAuthorizationCode}>
                                    {t('Security.Button.GenerateCode')}
                                </Button>
                            </div>
                            {securitySettings.anonymousViewerAuthorizationCode && (
                                <div className="pl-10 mb-4">
                                    {t('Security.AnonymousViewerLinkLabel')}
                                    <ShareEventWidget
                                        url={`${globalThis.window.location.origin}/${webinarId}/a/${securitySettings.anonymousViewerAuthorizationCode}`}
                                    />
                                </div>
                            )}
                            <p className="border-2 border-primary rounded-lg mb-8 ml-10 p-4">
                                <em>{t('Security.InfoText.Important')}</em>
                                <br />
                                {t('Security.AllowAnonymousWarning')}
                            </p>
                        </>
                    )}
                    <div className="flex flex-col md:flex-row items-start gap-4 pt-8">
                        <div className="w-full bg-white rounded-lg p-4">
                            <div className="flex items-center justify-center mb-4">
                                <Close classNames="size-6 stroke-warning mr-2" />
                                <h2 className="font-accent text-2xl">
                                    {t('Security.Filter.Disallow')} {disallowFilter ? `(${disallowFilter.split('\n').length})` : ''}
                                </h2>
                            </div>

                            <div className="group relative w-full">
                                <textarea
                                    className="w-full min-h-20 bg-transparent outline-hidden py-input-vert text-base"
                                    value={disallowFilter}
                                    onChange={(e) => updateDisallow(e.target.value)}
                                />
                                <div className="bg-black group-hover:bg-primary group-focus-within:bg-primary input-border"></div>
                            </div>
                        </div>
                        <div className="w-full bg-white rounded-lg p-4">
                            <div className="flex items-center justify-center mb-4">
                                <RightCheck classNames="h-5 w-5 fill-success mr-2" />
                                <h2 className="font-accent text-2xl text-center">
                                    {t('Security.Filter.Allow')} {allowFilter ? `(${allowFilter.split('\n').length})` : ''}
                                </h2>
                            </div>

                            <div className="group relative w-full">
                                <textarea
                                    className="w-full min-h-20 bg-transparent outline-hidden py-input-vert text-base"
                                    value={allowFilter}
                                    onChange={(e) => updateAllow(e.target.value)}
                                />
                                <div className="bg-black group-hover:bg-primary group-focus-within:bg-primary input-border"></div>
                            </div>
                        </div>
                    </div>

                    {(allowFilter || disallowFilter) && (
                        <div className="relative bg-white rounded-lg mt-8 p-4">
                            <h2 className="font-accent text-2xl text-center mb-4">{t('Security.FilterResults.Heading')}</h2>
                            <div className="table-responsive">
                                <table className="table table-striped whitespace-nowrap">
                                    <thead>
                                        <tr>
                                            <th>{t('Security.FilterResults.Table.Header.Email')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>test@gmail.com</td>
                                            {isCheckingIsAllowed && <td>Checking</td>}
                                            {!isCheckingIsAllowed && (
                                                <td>
                                                    {checkResults.filter((x) => x.emailAddress === 'test@gmail.com')[0]?.isAllowed === true && 'Allowed'}
                                                    {checkResults.filter((x) => x.emailAddress === 'test@gmail.com')[0]?.isAllowed === false && 'Disallowed'}
                                                </td>
                                            )}
                                        </tr>
                                        <tr>
                                            <td>test@outlook.com</td>
                                            {isCheckingIsAllowed && <td>Checking</td>}
                                            {!isCheckingIsAllowed && (
                                                <td>
                                                    {checkResults.filter((x) => x.emailAddress === 'test@outlook.com')[0]?.isAllowed === true && 'Allowed'}
                                                    {checkResults.filter((x) => x.emailAddress === 'test@outlook.com')[0]?.isAllowed === false && 'Disallowed'}
                                                </td>
                                            )}
                                        </tr>
                                        <tr>
                                            <td>noreply@onlinewebinar.com</td>
                                            {isCheckingIsAllowed && <td>Checking</td>}
                                            {!isCheckingIsAllowed && (
                                                <td>
                                                    {checkResults.filter((x) => x.emailAddress === 'noreply@onlinewebinar.com')[0]?.isAllowed === true &&
                                                        'Allowed'}
                                                    {checkResults.filter((x) => x.emailAddress === 'noreply@onlinewebinar.com')[0]?.isAllowed === false &&
                                                        'Disallowed'}
                                                </td>
                                            )}
                                        </tr>
                                        {account?.profile?.emailAddress && (
                                            <tr>
                                                <td>{account.profile.emailAddress}</td>
                                                {isCheckingIsAllowed && <td>Checking</td>}
                                                {!isCheckingIsAllowed && (
                                                    <td>
                                                        {checkResults.filter((x) => x.emailAddress === account.profile.emailAddress)[0]?.isAllowed === true &&
                                                            'Allowed'}
                                                        {checkResults.filter((x) => x.emailAddress === account.profile.emailAddress)[0]?.isAllowed === false &&
                                                            'Disallowed'}
                                                    </td>
                                                )}
                                            </tr>
                                        )}
                                        <tr>
                                            <td>
                                                <Input
                                                    label={t('Security.FilterResults.Label.TestResults')}
                                                    value={customEmailAddress}
                                                    onChange={(e) => {
                                                        setCustomEmailAddress(e.target.value);
                                                    }}
                                                    classNames="mb-0"
                                                />
                                            </td>
                                            {customEmailAddress && (
                                                <>
                                                    {isCheckingIsAllowed && <td>Checking</td>}
                                                    {!isCheckingIsAllowed && (
                                                        <td>
                                                            {checkResults.filter((x) => x.emailAddress === customEmailAddress)[0]?.isAllowed === true &&
                                                                'Allowed'}
                                                            {checkResults.filter((x) => x.emailAddress === customEmailAddress)[0]?.isAllowed === false &&
                                                                'Disallowed'}
                                                            {checkResults.filter((x) => x.emailAddress === customEmailAddress)[0]?.isAllowed === undefined &&
                                                                'Checking'}
                                                        </td>
                                                    )}
                                                </>
                                            )}
                                            {!customEmailAddress && <td></td>}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className="border-2 border-primary rounded-lg mt-8 p-4">
                        <em>{t('Security.InfoText.Important')}</em>
                        <br />
                        <ul className="list-disc ps-8 pt-2">
                            <li className="pb-2">
                                <Trans i18nKey="Security.InfoText.Item1" />
                            </li>
                            <li>{t('Security.InfoText.Item2')}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </ContainerNoTopSpace>
    );
};
